import { useEffect, useEffectEvent } from 'react'
import { type ParticipatingVotingRegionsResponse } from '../../../../functions/audience/participating-voting-regions/index.js'
import { ErrorUserMessages } from '../../../../shared/errors.js'
import { Icon } from '../../assets/icons/index.js'
import { Note } from '../../components/Note.js'
import { useTrack } from '../../hooks/useTrack.js'
import { type VotingRegion } from '../../hooks/useVotingRegion.js'
import { MOBILE } from '../../utils/breakpoints.js'
import { colors } from '../../utils/colors.js'

export const ErrorNote = ({
  error,
  dictionary,
  participatingVotingRegions,
  votingRegion,
  setVotingRegion,
}: {
  error: string | null
  dictionary: Record<string, string>
  participatingVotingRegions: ParticipatingVotingRegionsResponse
  votingRegion: VotingRegion
  setVotingRegion: (code: string) => void
}) => {
  const track = useTrack('error-note')
  if (
    !error ||
    error === ErrorUserMessages.InvalidEmail ||
    error === ErrorUserMessages.DisposableEmail
  ) {
    return null
  }

  let errorMessage: React.ReactNode
  let errorLocationCode: string | undefined
  const parts = error.split('::')

  if (parts[0] === 'Stripe') {
    errorMessage = parts[1]
    if (parts[2]) {
      errorLocationCode = parts[2]
    }
  } else if (parts[0] === ErrorUserMessages.CardNotEligible) {
    try {
      const cardCountry = parts[1]
      if (!cardCountry || !votingRegion.name) {
        throw new Error('No cardGeo or no votingRegion name')
      }
      const newVotingRegion =
        participatingVotingRegions[cardCountry]?.isTerritoryOf?.name ||
        participatingVotingRegions[cardCountry]?.name ||
        'ROW'

      errorMessage = dictionary.errorWrongRegion
        .replace(
          /{{CARD_COUNTRY}}/g,
          participatingVotingRegions[cardCountry]?.displayName || cardCountry,
        )
        .replace(/{{VOTING_REGION}}/g, votingRegion.name)

      const m = /(.*?){{SWITCH_COUNTRY:(.*?)}}(.*)/.exec(errorMessage as string)

      const switchVotingRegion = () => {
        setVotingRegion(newVotingRegion)
      }

      if (m) {
        errorMessage = [
          m[1],
          <span
            key={1}
            style={{
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            onClick={switchVotingRegion}
            onTouchStart={switchVotingRegion}
          >
            {m[2]}
          </span>,
          m[3],
        ]
      }
      if (parts[2]) {
        errorLocationCode = parts[2]
      }
    } catch {
      errorMessage = dictionary.errorGeneric
      errorLocationCode = '2002'
    }
  } else if (parts[0] === ErrorUserMessages.CaptchaError) {
    errorMessage = dictionary.errorCaptcha
  } else if (
    parts[0] === ErrorUserMessages.CardOrWalletAlreadyVotedInSequence
  ) {
    errorMessage = dictionary.errorCardAlreadyUsed
  } else if (parts[0] === ErrorUserMessages.EmailAlreadyUsedInSequence) {
    errorMessage = dictionary.errorEmailAlreadyUsed
  } else {
    errorMessage = dictionary.errorGeneric
    errorLocationCode = '2001'
  }

  useEffect(() => {
    const target = `${errorLocationCode || ''}-${errorMessage}`.slice(0, 32)
    track('processing_payment', target)
  }, [])

  return (
    <Note
      color="red"
      leadIcon={<Icon type="errorIcon" color={colors.red} />}
      style={{
        margin: '0 48px',
        [MOBILE]: {
          margin: '0px 24px',
        },
      }}
    >
      {errorMessage}
      {errorLocationCode && (
        <span style={{ opacity: 0.3 }}>
          &nbsp;&nbsp;&nbsp;({errorLocationCode})
        </span>
      )}
    </Note>
  )
}

export const EmailErrorNote = ({
  error,
  dictionary,
}: {
  error: string | null
  dictionary: Record<string, string>
}) => {
  const track = useTrack('error-note-email')

  if (
    !error ||
    (error !== ErrorUserMessages.InvalidEmail &&
      error !== ErrorUserMessages.DisposableEmail)
  ) {
    return null
  }

  let errorMessage: string = '---'

  if (error === ErrorUserMessages.InvalidEmail) {
    errorMessage = dictionary.errorInvalidEmail || 'Invalid email'
  } else if (error === ErrorUserMessages.DisposableEmail) {
    errorMessage =
      dictionary.errorInvalidEmailDomain || 'Blocked email provider'
  }

  useEffect(() => {
    const target = `${error}`.slice(0, 32)
    track('processing_payment', target)
  }, [])

  return (
    <Note
      color="red"
      leadIcon={<Icon type="errorIcon" color={colors.red} />}
      style={{
        maxWidth: 'none',
      }}
    >
      {errorMessage}
    </Note>
  )
}
